
import { PropType } from 'vue'
import { uniqueId, isEmpty, debounce } from 'lodash'
import NrFormLabel from '../form-elements/nr-form-label.vue'

interface ValidationProp {
  [key: string]: { message: string; validate(value: string): Promise<boolean> }
}

const COMPONENT_NAME = 'nr_form_input'

export default {
  name: COMPONENT_NAME,
  components: { NrFormLabel },
  emits: ['update:modelValue', 'input', 'keydown', 'blur', 'on-validate'],
  props: {
    help: String,
    hideLabel: { type: Boolean, default: false },
    label: String,
    name: String,
    required: Boolean,
    description: String,
    type: { type: String, default: 'text' },
    maxLength: { type: String, default: '300' },
    minLength: { type: String },
    tag: { type: String, default: 'input' },
    validations: {
      type: Object as PropType<ValidationProp>,
      default() {
        return {}
      }
    },
    iconTooltipText: { type: String },
    withTooltip: { type: Boolean, default: false },
    modelValue: { type: [String, Number, Boolean], default: '' }
  },

  data() {
    return {
      id: uniqueId(`${COMPONENT_NAME}-`),
      errors: {}
    }
  },

  mounted() {
    this.validate = debounce(this.validate, 300)
  },

  methods: {
    validate() {
      Object.keys(this.computedValidations).forEach(async (key) => {
        try {
          const isValid = await this.computedValidations[key].validate(
            this.modelValue
          )
          if (isValid) {
            delete this.errors[key]
            // This lets Vue that the object has changed.
            this.errors = Object.assign({}, this.errors)
          } else {
            throw new Error(`${key} is not valid.`)
          }

          this.$emit('on-validate', true)
        } catch (_error) {
          this.$emit('on-validate', false)
          this.errors = Object.assign({}, this.errors, {
            [`${key}`]: this.computedValidations[key].message
          })
        }
      })
    },

    handleInput(event) {
      this.validate()
      const value =
        this.type === 'checkbox' ? event.target.checked : event.target.value
      this.$emit('update:modelValue', value)
      this.$emit('input', value)
    },

    handleKeydown(event) {
      this.validate()
      this.$emit('keydown', event)
    },

    handleBlur(event) {
      this.validate()
      this.$emit('blur', event)
    }
  },

  computed: {
    labelClassnames() {
      return [
        'nr-label',
        this.hideLabel ? 'is-sr-only' : '',
        this.required ? 'required-label' : ''
      ]
    },

    inputClassNames(): string[] {
      const classes: string[] = []
      if (this.tag === 'textarea') {
        classes.push('nr-textarea')
      } else {
        classes.push('nr-input')
        switch (typeof this.modelValue) {
          case 'string':
            classes.push(this.modelValue?.length ? 'nr-input--filled' : '')
            break
          case 'number':
            classes.push(this.modelValue > 0 ? 'nr-input--filled' : '')
            break
          case 'boolean':
            classes.push(this.modelValue !== null ? 'nr-input--filled' : '')
            break
          default:
            break
        }
      }
      classes.push(this.errors && !isEmpty(this.errors) ? 'is-danger' : '')
      return classes
    },

    computedValidations() {
      let validationsComputed = { ...this.validations }
      if (this.required) {
        validationsComputed = Object.assign({}, validationsComputed, {
          required: {
            message: 'This field is required.',
            validate(value?: string) {
              return !!(String(value) || '').trim()
            }
          }
        })
      }

      if (this.minLength) {
        const minLength = this.minLength
        validationsComputed = Object.assign({}, validationsComputed, {
          minLength: {
            message: `Minimum length of ${minLength} required.`,
            validate(value?: string) {
              if (!value) {
                return true
              }

              return value.length >= parseInt(minLength)
            }
          }
        })
      }

      return validationsComputed
    },

    passthroughListeners() {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { input, keydown, blur, ...others } = this.$attrs
      return others
    }
  },

  watch: {
    modelValue() {
      this.validate()
    }
  }
}
